'use client';

import type { ComponentPropsWithoutRef } from 'react';
import React, { useRef } from 'react';
import { useRegisterPopoverScrollRef } from './hooks/usePopoverScrollRef';
import { cx } from '../../../utils/cx';
import { Input, type InputProps } from '../input';
import { styles } from './popoverStyles';

const ROMA_POPOVER_BODY_DISPLAY_NAME = 'RomaPopoverBody';
const ROMA_POPOVER_HEADER_DISPLAY_NAME = 'RomaPopoverHeader';
const ROMA_POPOVER_FOOTER_DISPLAY_NAME = 'RomaPopoverFooter';
const ROMA_POPOVER_SEARCH_DISPLAY_NAME = 'RomaPopoverSearch';

export type PopoverBodyProps = ComponentPropsWithoutRef<'div'>;

/**
 * PopoverBody is a scrollable container for the main content of the Popover.
 * It registers itself with the Popover context so the Popover can manage scroll-related behavior,
 * for example tracking scroll position or updating/repositioning the Popover in response to scrolling.
 */
export const PopoverBody: React.FC<PopoverBodyProps> = ({ className, children, ...props }) => {
    const ref = useRef<HTMLDivElement>(null);
    useRegisterPopoverScrollRef(ref);

    return (
        <div
            {...props}
            ref={ref}
            className={cx(ROMA_POPOVER_BODY_DISPLAY_NAME, styles.body, className)}
        >
            {children}
        </div>
    );
};

PopoverBody.displayName = ROMA_POPOVER_BODY_DISPLAY_NAME;

export type PopoverHeaderProps = ComponentPropsWithoutRef<'div'>;

/**
 * PopoverHeader is a container for the header content of the Popover.
 */
export const PopoverHeader: React.FC<PopoverHeaderProps> = ({ className, children, ...props }) => (
    <div {...props} className={cx(ROMA_POPOVER_HEADER_DISPLAY_NAME, styles.header, className)}>
        {children}
    </div>
);

PopoverHeader.displayName = ROMA_POPOVER_HEADER_DISPLAY_NAME;

export type PopoverFooterProps = ComponentPropsWithoutRef<'div'>;

/**
 * PopoverFooter is a container for the footer content of the Popover.
 * It is typically used for actions related to the content in the Popover, such as buttons for confirming or canceling an action.
 */
export const PopoverFooter: React.FC<PopoverFooterProps> = ({ className, children, ...props }) => (
    <div {...props} className={cx(ROMA_POPOVER_FOOTER_DISPLAY_NAME, styles.footer, className)}>
        {children}
    </div>
);

PopoverFooter.displayName = ROMA_POPOVER_FOOTER_DISPLAY_NAME;

export type PopoverSearchProps = Omit<InputProps, 'ref'> & {
    containerClassName?: string;
};

/**
 * PopoverSearch is a specialized input component designed for use within a Popover, typically in the header section.
 * It provides a styled search input that fits the design of the Popover and includes auto-focus behavior by default.
 */
export const PopoverSearch: React.FC<PopoverSearchProps> = ({
    containerClassName,
    wrapperClassName,
    inputClassName,
    autoFocus = true,
    ...props
}) => (
    <div className={cx(ROMA_POPOVER_SEARCH_DISPLAY_NAME, styles.search.container, containerClassName)}>
        <Input
            {...props}
            wrapperClassName={wrapperClassName}
            inputClassName={cx(styles.search.input, inputClassName)}
            autoFocus={autoFocus}
        />
    </div>
);

PopoverSearch.displayName = ROMA_POPOVER_SEARCH_DISPLAY_NAME;
